import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { useAuthorizationStore } from '@stores/authorizationStore'
import { useUserStore } from '@stores/userStore'

import { AxiosErrorHandler } from '@models/helpers/axiosErrorHandler'
import { type PointType } from '@models/entities/points'

import api from '@/axios/api'
import mocks from '@/dev/mocks'

export const usePointsStore = defineStore('usePointsStore', () => {
  const authStore = useAuthorizationStore()
  const userStore = useUserStore()

  const loading = ref(true)
  const pointList = ref<PointType[]>([])
  const pointForInvoiceCreation = ref<PointType | null>(null)
  const pointListFiltered = ref<PointType[]>([])

  const availablePointList = computed<PointType[]>(() => userStore.isOwnerUser
    ? pointList.value
    : pointList.value
      .filter(point => userStore.availableSalepoints
        .some(salepoint => (salepoint.code === point.code) && salepoint.checked)))

  const _setLoading = (status: boolean = true) => {
    loading.value = status
  }

  const setPointForInvoiceCreation = (point: PointType | null) => {
    pointForInvoiceCreation.value = point
  }

  const _fetchPoints = async (
    target: typeof pointList | typeof pointListFiltered,
    params?: Record<string, string | string[] | number | boolean | undefined>
  ) => {
    _setLoading()

    if (import.meta.env.MODE === 'development') {
      target.value = mocks.point_list
      _setLoading(false)
      return Promise.resolve()
    }

    await api
      .get('/personal-api/v1/points/', params ? { params } : {})
      .then((response) => {
        if (response.data) target.value = response.data
      })
      .catch((error) => {
        const errorHandler = new AxiosErrorHandler(error)
        if (errorHandler.isLogoutError) {
          authStore.setAuthorizationStatus(false)
        }
      })
      .finally(() => {
        _setLoading(false)
      })
  }

  const getPoints = async () => {
    await _fetchPoints(pointList)
  }

  const getPointsFiltered = async (params: Record<string, string | string[] | number | boolean | undefined> = {}) => {
    await _fetchPoints(pointListFiltered, params)
  }

  const reset = () => {
    pointList.value = []
    pointListFiltered.value = []
    loading.value = true
  }

  return {
    loading,
    pointList,
    pointListFiltered,
    pointForInvoiceCreation,
    availablePointList,
    setPointForInvoiceCreation,
    getPointsFiltered,
    reset,
    getPoints
  }
})
